import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { KTIcon } from '../../_metronic/helpers'
import { RootState } from '../core/store/store'
import searchTalentRequest from '../core/api/requests/search/searchTalentRequest'
import IAgencyTalent from '../core/interfaces/IAgencyTalent'
import TalentBigButton from '../utils/TalentBigButton'
import ITalent from '../core/interfaces/ITalent'

interface Props {
  show: boolean
  onClose: () => void
}

const DEBOUNCE_MS = 300

const ModalTalentCommandPalette: FC<Props> = ({ show, onClose }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const initialKey = useSelector((state: RootState) => state.searcher.commandPaletteInitialKey)

  const [query, setQuery] = useState('')
  const [talents, setTalents] = useState<IAgencyTalent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchIdRef = useRef(0)
  const justOpenedRef = useRef(false)

  const searchTalents = useCallback(
    async (searchQuery: string) => {
      const id = ++searchIdRef.current
      setLoading(true)
      setError(null)
      try {
        const filters = { query: searchQuery, page: 1, warningsDate: new Date().toISOString().split('T')[0] }
        const response = await searchTalentRequest(dispatch, filters)

        if (id !== searchIdRef.current) return

        if (response?.statusCode !== 200) {
          const msg =
            (response?.data as { message?: string })?.message ||
            intl.formatMessage({ id: 'ERRORS.SOMETHING_WAS_WRONG_TRY_AGAIN_LATER' })
          setError(msg)
          setTalents([])
          return
        }

        const data = response?.data?.data ?? []
        setTalents(Array.isArray(data) ? data : [])
      } catch (e: unknown) {
        if (id !== searchIdRef.current) return
        const msg =
          e instanceof Error ? e.message : intl.formatMessage({ id: 'ERRORS.SOMETHING_WAS_WRONG_TRY_AGAIN_LATER' })
        setError(msg)
        setTalents([])
      } finally {
        if (id === searchIdRef.current) setLoading(false)
      }
    },
    [dispatch, intl]
  )

  useEffect(() => {
    if (!show) return

    setQuery(initialKey)
    setError(null)
    justOpenedRef.current = true
    searchTalents(initialKey)
  }, [show, searchTalents, initialKey])

  useEffect(() => {
    if (!show) return

    if (justOpenedRef.current) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      searchTalents(query)
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [show, query, searchTalents])

  const handleSelectTalent = useCallback(
    (talent: ITalent) => {
      if (talent?.id) {
        onClose()
        navigate(`/talents/${talent.id}`)
      }
    },
    [onClose, navigate]
  )

  useEffect(() => {
    if (!show) return

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [show, onClose])

  return (
    <Modal
      className="modal-transparent-backdrop"
      id="kt_modal_talent_command_palette"
      aria-labelledby="kt_modal_talent_command_palette_label"
      aria-hidden="true"
      show={show}
      onHide={onClose}
      dialogClassName="modal-dialog-centered modal-lg"
      backdrop={false}
    >
      <div className="modal-content shadow">
        <div className="modal-header border-0 pb-0">
          <div className="d-flex align-items-center w-100 gap-2">
            <span className="text-muted">
              <KTIcon iconName="magnifier" className="fs-2" />
            </span>
            <input
              autoFocus
              type="text"
              className="form-control form-control-solid form-control-lg border-0 bg-transparent"
              placeholder={intl.formatMessage({ id: 'PLACEHOLDERS.TALENT_SEARCH' })}
              value={query}
              onChange={(e) => {
              justOpenedRef.current = false
              setQuery(e.target.value)
            }}
              aria-label={intl.formatMessage({ id: 'PLACEHOLDERS.TALENT_SEARCH' })}
            />
          </div>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
            onClick={onClose}
            aria-label="Close"
          >
            <KTIcon iconName="cross" className="fs-2" />
          </button>
        </div>

        <div className="modal-body pt-0 overflow-auto" style={{ maxHeight: '60vh' }}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center py-15">
              <span className="spinner-border spinner-border-lg text-primary" role="status" />
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger d-flex align-items-center">
              <KTIcon iconName="information-5" className="fs-2hx text-danger me-4" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && talents.length === 0 && (
            <div className="text-center py-15">
              <p className="text-muted fs-5 mb-0">
                {intl.formatMessage({ id: 'ALERTS.NO_TALENTS_FOUND' })}
              </p>
              <p className="text-gray-500 fs-7 mt-2">
                {intl.formatMessage({ id: 'ALERTS.NO_RESULTS_FOUND_TRY_ANOTHER_SEARCH' })}
              </p>
            </div>
          )}

          {!loading && !error && talents.length > 0 && (
            <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3">
              {talents.map((item) => (
                <TalentBigButton
                  key={item.talent.id}
                  talent={item.talent}
                  out={item.out}
                  showDetails
                  allowSelect={false}
                  onSelect={handleSelectTalent}
                />
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer border-0 pt-0">
          <span className="text-muted fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.COMMAND_PALETTE_HINT' })}</span>
        </div>
      </div>
    </Modal>
  )
}

export { ModalTalentCommandPalette }

